// src/services/chats.js — chat list + per-chat message stream
import { isNewerThan } from './lastMessage';
import { mergeLive } from './liveMessages';
import {
  collection, query, where, orderBy, onSnapshot, doc, getDoc, limitToLast } from 'firebase/firestore';
import { db } from '../config/firebase';
import { orderMessages } from './messageOrder';
import { isGhostRow } from './ghostRows';

/* ═══ STRAY GUEST-CALL ROWS ════════════════════════════════════════════════
   A guest who came through a CHAT link has a real chat document, stamped
   `isGuest` by /api/guest/join, and it belongs in this list. A guest who came
   through a CALL link has no chat at all — they are a browser tab with a
   countdown. Older app builds authored a chat document while writing the call
   bubble, so calling a link guest left a permanent row here for somebody who is
   deleted the moment their link expires.

   The peer's `isGuest` is on their USER document, not on the chat, so this needs
   a read. Cached per uid for the lifetime of the tab: the answer cannot change
   (a guest never becomes an account) and this runs on every snapshot.

   🟥 FAIL-OPEN. An unresolved or unreadable peer is KEPT. The failure this
   protects against is a real conversation blinking out of somebody's list
   because one read was slow, which is far worse than the stray row it is
   cleaning up staying one more snapshot. */
const _peerIsGuest = new Map(); // uid → true | false

function _strayGuestRow(chat, uid) {
  if (!chat || chat.isGroup || chat.isGuest) return false;
  const other = otherParticipant(chat, uid);
  if (!other) return false;
  return _peerIsGuest.get(other) === true;
}

async function _resolvePeers(chats, uid) {
  const want = [];
  for (const c of chats) {
    if (!c || c.isGroup || c.isGuest) continue;
    const other = otherParticipant(c, uid);
    if (other && !_peerIsGuest.has(other)) want.push(other);
  }
  if (!want.length) return false;
  await Promise.all(want.map(async (id) => {
    try {
      const s = await getDoc(doc(db, 'users', id));
      /* 🟥 A MISSING USER IS NOT A GUEST. An expired guest's document is deleted,
         so `!exists` looks like the strongest possible signal — but it is also
         what a Firestore rules denial and a half-migrated account look like, and
         a wrong `true` here silently removes a real conversation. Only the flag
         itself counts. */
      _peerIsGuest.set(id, s.exists() && s.data()?.isGuest === true);
    } catch {
      _peerIsGuest.set(id, false);
    }
  }));
  return true;
}

/** Live list of the user's chats, most-recent first. */
export function watchChats(uid, cb) {
  const q = query(
    collection(db, 'chats'),
    where('participants', 'array-contains', uid)
  );
  let gen = 0;
  const stop = onSnapshot(q, (snap) => {
    const mine = ++gen;
    const chats = snap.docs
      /* 🟥 THE ROW THAT JUMPS DOWN AND COMES BACK. `d.data()` reports an
         UNRESOLVED serverTimestamp() as **null**, and every send writes the chat
         doc's `timestamp` with exactly that. So for the ~200ms between the local
         write and the server's ack — precisely the single-checkmark window the
         report names — this chat's sort key was not "now", it was whatever older
         stamp `lastActivityMs` could still find (createdAt), and the row fell to
         where the conversation had been before anyone spoke. The ack landed, the
         real stamp appeared, and it climbed back. Nothing was wrong with the
         write, the sort, or the render: the value simply was not there yet.

         🟢 'estimate' IS FIRESTORE'S OWN ANSWER TO THIS, and it is the same
            answer messageOrder.js already gives on the message list (`pendingMs`,
            "treating that as 0 would sort a brand-new message to the very top").
            A pending stamp reads as this device's local time instead of null, so
            the row sorts as what it is — the newest thing that happened — from
            the first frame. When the server resolves, the value is replaced by
            the real one and nothing moves, because a few milliseconds of drift
            cannot reorder a list whose neighbours are seconds or minutes apart.

         🟥 AND IT MUST BE ON THIS READ, not patched into lastActivityMs. That
            function is given a plain object and cannot tell "no timestamp yet"
            from "no timestamp ever" — a chat with no messages must still sort by
            createdAt, and guessing `now` for it would shove every empty chat to
            the top of the list. Only the snapshot knows which it is. */
      .map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) }))
      // 🟢 DELETED ON THE PHONE = GONE HERE. Long-press → Delete writes
      //    chats/{id}.deletedFor[uid] = true (a MAP on the chat doc — message docs use
      //    an array for the same field name, so handle both). Without this the web
      //    kept listing chats the user had deleted.
      .filter((c) => !isDeletedFor(c, uid))
      // 🟢 Allo AI lives on the phone only — the assistant runs in the app, so a row
      //    here would open a chat the web can neither continue nor answer.
      .filter((c) => !isAiChat(c));
    // 🟢 PINS COME FROM THE PHONE. Long-press → Pin writes `chats/{id}.pinned[uid] = true`
    //    on the chat doc, so there is nothing to sync — the field is already here, the
    //    web just never looked at it. Pinned first, then newest-first inside each tier,
    //    which is the order the app's list uses.
    const pin = (c) => (c?.pinned?.[uid] ? 1 : 0);
    chats.sort((a, b) => (pin(b) - pin(a)) || (lastActivityMs(b) - lastActivityMs(a)));
    /* Paint from what is already known FIRST — on the second and every later
       snapshot the cache is warm and this is the only emit that happens. Then
       re-emit only if a lookup actually ran, and only if no newer snapshot has
       arrived meanwhile (`mine === gen`), so a late read can never overwrite a
       fresher list with an older one. */
    cb(chats.filter((c) => !_strayGuestRow(c, uid)));
    _resolvePeers(chats, uid).then((learned) => {
      if (learned && mine === gen) cb(chats.filter((c) => !_strayGuestRow(c, uid)));
    });
  });
  return () => { gen += 1; stop(); };
}

/** Live message stream for a chat, oldest→newest, capped to a recent window. */
export function watchMessages(chatId, cb, { limitCount = 200, sinceMs = 0 } = {}) {
  if (!chatId) { cb([]); return () => {}; } // guard: never build a query with an undefined id
  /* 🟥 THE WHOLE COLLECTION WAS READ, AND SLICED TO 200 HERE. A chat that is never
     swept (an official account, a broadcast, a long history on a slow sweep) sent
     every message it ever had on every open — and re-sorted all of them on every
     snapshot. The server sends the newest window (with a margin for re-ordering by
     seq below); the slice still decides the exact 200. */
  /* 👥 `sinceMs`: a group I was ADDED to starts at my join (services/joinFloor) — nothing earlier is
        asked for (it is sealed for the members of the time, and showed as a 🔒 per message) */
  const q = sinceMs > 0
    ? query(collection(db, 'chats', chatId, 'messages'), where('timestamp', '>', new Date(sinceMs)), orderBy('timestamp', 'asc'), limitToLast(limitCount + 50))
    : query(
    collection(db, 'chats', chatId, 'messages'),
    orderBy('timestamp', 'asc'),
    limitToLast(limitCount + 50)
  );
  /* 🟥 THE LIST WAS REBUILT FROM THE SNAPSHOT, so a message the phone had just
     swept vanished from the screen — "it shows up immediately, then disappears,
     then re-appears on the next sync". The sweep is a handoff, not a deletion.
     What has been seen is kept until a tombstone says otherwise; see
     services/liveMessages.js. */
  let held = [];
  /* ✓ includeMetadataChanges: a row the server has not acknowledged yet (our own write, echoed back
     from the local queue) is marked `_pendingWrite` — and told again when the server commits it, so
     the tick can wait for the server instead of believing the echo (services as on the phone). */
  return onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
    const fresh = snap.docs.map((d) => ({ id: d.id, ...d.data(), _pendingWrite: d.metadata.hasPendingWrites === true }));
    held = mergeLive(held, fresh);
    /* 🟥 orderBy('timestamp') IS THE QUERY'S ORDER, NOT THE CONVERSATION'S. The
       phone orders by the server's per-chat `seq`, and two clocks do not agree
       — so the same chat could read in a different order here than it does
       there. Re-order by seq (see messageOrder.js), which is the key both
       platforms can actually share.

       🟥 AND THE ORDER HAS TO COME FIRST NOW. The slice takes the RECENT 200, and
          `held` is a merge of retained rows and fresh ones — its own order means
          nothing, so slicing it before sorting would take an arbitrary 200. */
    const ordered = orderMessages(held);
    cb(ordered.length > limitCount ? ordered.slice(ordered.length - limitCount) : ordered);
  });
}

export async function getChat(chatId) {
  const snap = await getDoc(doc(db, 'chats', chatId));
  return snap.exists() ? { id: chatId, ...snap.data() } : null;
}

export function otherParticipant(chat, myUid) {
  if (!chat?.participants) return null;
  return chat.participants.find((p) => p !== myUid) || null;
}

/** The Allo AI assistant chat — app-only. */
export function isAiChat(chat) {
  if (!chat || chat.isGroup) return false;
  return (chat.participants || []).includes('allo-ai-bot') || String(chat.id || '').includes('allo-ai-bot');
}

/** Note to Self — the chat whose only participant is you. `otherParticipant` returns
 *  null for it, which is why the row used to fall back to the generic "Allo user". */
export function isNoteToSelf(chat, myUid) {
  if (!chat || chat.isGroup || !myUid) return false;
  const p = chat.participants || [];
  return p.length > 0 && p.every((u) => u === myUid);
}

/** chats/{id}.deletedFor — a map uid→true on the CHAT doc, an array on MESSAGE docs. */
export function isDeletedFor(chatOrMsg, uid) {
  const df = chatOrMsg?.deletedFor;
  if (!df || !uid) return false;
  return Array.isArray(df) ? df.includes(uid) : df[uid] === true;
}

/** 🟢 chats/{id}.clearedAt[uid] — the per-user watermark the phone writes on
 *  "Clear chat" AND on "Delete chat" (ChatInfoScreen.performClearChatCore /
 *  ChatsTab.handleDeleteChat). The phone hides everything at or before it; so must we,
 *  or the web shows messages the user already cleared. */
export function clearedAtMs(chat, uid) {
  const c = chat?.clearedAt;
  if (!c || !uid) return 0;
  return tsMs(c[uid]);
}

/**
 * When did this conversation last move? — the chat list's sort key.
 *
 * 🟥 THE LIST WAS SORTED BY A FIELD NOTHING WRITES ANY MORE. This used to read
 *    `lastMessageTime || updatedAt`. Every send path in this app was later
 *    corrected to write `timestamp` instead, to match the phone — see the long
 *    note in services/messages.js, which says in as many words that
 *    `lastMessageTime` is "a field NOTHING on the phone reads" and that ChatsTab
 *    sorts and displays by `timestamp`. The WRITES were fixed; this READ was not.
 *
 *    So the web was sorting on a value that is absent on most chat documents and
 *    frozen at chat-creation time on the rest. tsMs() returns 0 for a missing
 *    field, which made the comparator return 0 for nearly every pair — the list
 *    kept whatever order the Firestore snapshot happened to arrive in and never
 *    reordered for anything. That is both "a message from the app doesn't move
 *    the chat up" and the earlier "a guest who joins lands in a random position":
 *    the join writes `timestamp` on the chat doc (server/functions/guestLinks.js)
 *    and the web was not looking at it.
 *
 * 🟢 MAX, NOT A `||` CHAIN. A document written by an older build can carry BOTH
 *    a stale `lastMessageTime` and a fresh `timestamp`; first-non-empty would
 *    pick whichever the chain happened to name first and could hand back the
 *    older of the two. The most recent stamp is the honest answer, whichever
 *    field it happens to live in — and this keeps working if a future writer
 *    adds another one.
 *
 *    `createdAt` is included so a chat that exists but has no messages yet sorts
 *    by when it was made rather than falling to the bottom on 0. It can never
 *    override real activity: a later message's stamp is always the larger value.
 */
export function lastActivityMs(chat) {
  return Math.max(
    tsMs(chat?.timestamp),
    tsMs(chat?.lastMessageTime),
    tsMs(chat?.updatedAt),
    tsMs(chat?.createdAt),
    eventMs(chat),
  );
}

/* 👍 A REACTION (OR A VOTE) IS ACTIVITY. The phone writes it to the chat document as
   an EVENT — the marker plus `lastEventAt` — and not as `timestamp`, which stays the
   newest MESSAGE's time. A live event marker therefore moves the chat to the top and,
   while it is newer than the message the row would quote, owns the row's line and
   clock. (The phone's ChatsTab._eventMsOf is the same rule.) */
export function eventMs(chat) {
  const sys = liveSystemMarker(chat);
  if (!sys || (sys.type !== 'reaction' && sys.type !== 'vote')) return 0;
  return tsMs(chat?.lastEventAt);
}

const REACTION_TARGET = {
  photo: 'a photo', video: 'a video', voice: 'a voice message', location: 'a location',
  document: 'a document', poll: 'a poll', post: 'a shared post', photos: 'multiple photos',
  security: 'a login alert', sticker: 'a sticker',
};
/** What a reaction quotes, as the phone stamps it: a language-neutral kind. */
export function reactionTargetKind(m) {
  if (!m) return 'text';
  switch (m.type) {
    case 'image': return m.isSticker ? 'sticker' : 'photo';
    case 'video': return 'video';
    case 'audio': return 'voice';
    case 'location': return 'location';
    case 'document': return 'document';
    case 'poll': return 'poll';
    case 'shared_post': return 'post';
    case 'images': return 'photos';
    case 'security': return 'security';
    default: return 'text';
  }
}
/**
 * "You reacted ❤️ to: "hello"". A TEXT target's words are not on the shared chat
 * document (they were an encrypted message's plaintext); `lookupText(id)` quotes this
 * browser's own copy, and without one the line says "a message".
 */
export function reactionLine(sys, { isMe, lookupText } = {}) {
  if (!sys) return '';
  const who = isMe ? 'You' : (sys.name || 'Someone');
  let target = REACTION_TARGET[sys.targetKind] || sys.targetText || '';
  if (!target && sys.targetMessageId && typeof lookupText === 'function') {
    const tx = String(lookupText(String(sys.targetMessageId)) || '').trim();
    if (tx && !tx.startsWith('🔒')) target = tx.length > 30 ? `${tx.slice(0, 30)}...` : tx;
  }
  if (!target) target = 'a message';
  return `${who} reacted ${sys.emoji || ''} to: "${target}"`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 IS THE CHAT DOC'S EVENT MARKER STILL THE LATEST THING THAT HAPPENED?

   `lastMessageSystem` is how a pure chat-document EVENT — a poll vote, a
   reaction — describes the row. It has no message document of its own, so the
   row renders it INSTEAD of the newest message, and both clients treated it as
   automatically current. It is not. Ten different writers set `lastMessage`
   without retiring the marker beside it: forwarding into a group, sharing a
   post, the send-retry and transient-error fallbacks, the post-delete
   recompute. After any of those, the row keeps announcing "Abdullah voted: A"
   while a newer text message sits underneath it, unread and invisible.

   🟥 AND A TIMESTAMP CANNOT SETTLE IT. The obvious fix — "honour the marker
      only while it is newer than the newest message" — fails on exactly the
      case it is meant to catch. When a writer forgets to clear, its chat-doc
      write runs AFTER the message's own write, so `chat.timestamp` genuinely IS
      later than the message's. The marker is not merely stale-looking; it was
      written last. No clock can separate "an event happened after the message"
      from "a message writer orphaned the marker".

   🟢 THE EXACT SIGNAL IS THAT THE TWO FIELDS ARE WRITTEN TOGETHER. Every writer
      of `lastMessageSystem` sets `lastMessage` in the SAME operation, to the
      line that marker renders to. So if `lastMessage` no longer reads as this
      marker's line, some later writer moved the preview on and left the marker
      behind — which is proof, not an estimate.

      It needs no clocks, it repairs every document already broken in the field
      with no migration, it covers writers not yet written, and when the strings
      ever drift it fails toward showing the real message rather than toward the
      stuck row this exists to remove.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The `lastMessage` line a marker's writer puts on the chat document, verbatim.
 * `null` for markers this rule does not cover.
 *
 * 🟥 ONLY THE TWO PURE-EVENT MARKERS. A call or a screenshot notice also writes
 *    a real message document, so the ordinary newest-message path already
 *    describes it — and its `lastMessage` is a localized string that cannot be
 *    reconstructed from the marker at all. Guessing at those would trade a
 *    stuck row for a blank one.
 */
export function markerPreviewLine(sys) {
  if (!sys) return null;
  if (sys.type === 'vote') return `${sys.name} voted: ${sys.targetText}`;
  if (sys.type === 'reaction') return `Reacted ${sys.emoji} to: "${sys.targetText}"`;
  return null;
}

/**
 * The chat document's marker, or null when a later writer has orphaned it.
 * Anything this rule does not cover is passed through untouched.
 */
export function liveSystemMarker(chat) {
  const sys = chat?.lastMessageSystem;
  if (!sys?.type) return null;
  const line = markerPreviewLine(sys);
  if (line === null) return sys;                        // not covered — unchanged
  const last = chat?.lastMessage;
  if (typeof last !== 'string' || !last) return sys;    // nothing to disagree with
  /* 🟢 A PREFIX, NOT AN EQUALITY. Some writers slice `lastMessage` to a
     preview length, and a truncated copy of this marker's own line is still
     this marker's line — while a different message's text is not. */
  return last.startsWith(line.slice(0, 60)) ? sys : null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 "BEHIND" MEANT "THE STAMP MOVED", AND THE STAMP MOVES FOR THINGS THAT ARE
      NOT MESSAGES. The chat document's `timestamp` is bumped by a reaction, a
      poll vote, a screenshot marker and a group call ending — the phone writes
      it beside `lastMessageSystem` for every one of them. The row compared that
      stamp with the newest message it could quote, found the stamp newer, and
      announced that something was still coming from the phone. Nothing was: the
      newest event WAS the reaction, and no export will ever carry a message to
      explain it. That is the dot that appears on one chat and not another, on
      a chat "that already has its message", every time the sync runs.

   🟢 A ROW IS BEHIND WHEN A MESSAGE EXISTS THAT IT CANNOT QUOTE, and the chat
      document says so directly: `lastMessageId` is written by every message
      sender in the same operation as the message, and the event marker says
      when the newest thing is not a message at all. So the question has an
      exact answer — the marker is not current, the newest message id is not the
      one the row is quoting, and the document's clock agrees — instead of a
      guess made from a clock alone.
   ═══════════════════════════════════════════════════════════════════════════ */
/** Slack for the clock half: two writers, two clocks, one row. */
export const BEHIND_SLACK_MS = 30 * 1000;

/**
 * Is there a MESSAGE in this chat that the row is not quoting?
 * @param chat   the chat document
 * @param shown  what the row is quoting: {id?, ts?} (from newerPreview)
 */
export function messageMissing(chat, shown, slackMs = BEHIND_SLACK_MS) {
  if (!chat) return false;
  /* the newest thing that happened is an event, not a message — nothing to fetch */
  if (liveSystemMarker(chat)) return false;
  /* a chat with no stamp at all has activity 0 and fails the clock test below */
  const activity = lastActivityMs(chat);
  const quotedTs = Number(shown?.ts) || 0;
  const quotedId = shown?.id ? String(shown.id) : null;
  const newestId = chat.lastMessageId ? String(chat.lastMessageId) : null;
  /* 🟢 THE ID IS THE PROOF. If the row is quoting the very message the document
     names as newest, it is not behind, however the two clocks disagree. */
  if (newestId && quotedId && newestId === quotedId) return false;
  /* 🟥 AND THE CLOCK IS STILL REQUIRED. A chat document written by an older
     build may name a message id the export knows under its clientTempId; the
     clock is what stops that mismatch reading as "behind" for ever. */
  return activity > quotedTs + slackMs;
}

export function tsMs(t) {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  /* 🟢 THE NANOSECONDS ARE KEPT, as the phone's msOf keeps them: two rows from
     the same second used to collapse to the same millisecond here and rank
     in whatever order the array happened to hold them. */
  if (t.seconds) return t.seconds * 1000 + Math.floor((Number(t.nanoseconds) || 0) / 1e6);
  if (t.toDate) return t.toDate().getTime();
  const n = new Date(t).getTime();
  return Number.isFinite(n) ? n : 0;
}

// ══════════════════════════════════════════════════════════════════════════
//  UNREAD
// ══════════════════════════════════════════════════════════════════════════
//
// 🟥 `readUpTo[uid]` IS THE ONLY CROSS-DEVICE WATERMARK. The phone's chat-list badge is
//    NOT derived from `unreadCounts` — that field is legacy there, read once as a
//    migration seed and otherwise ignored. What the phone actually keeps is a local
//    read cursor, which it advances from `chats/{id}.readUpTo[myUid]` on every chats
//    snapshot (ChatsTab ~6492, forward-only). So: writing `unreadCounts[me] = 0` clears
//    the badge HERE and does nothing at all THERE. Advancing readUpTo clears both.
//
//    The web can't derive its own count the way the phone does, because 1:1 message
//    docs are deleted by the server's store-and-forward sweep once delivered — counting
//    surviving docs would undercount badly. So the badge reads `unreadCounts[me]`
//    (which every sender maintains and which survives the sweep) and uses readUpTo as
//    the veto: if we've read past the newest message, the count is stale, show nothing.

export function readUpToMs(chat, uid) {
  const v = chat?.readUpTo?.[uid];
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}

/** Mirrors the phone's unreadRules.isNonCountable — things that exist in the message
 *  stream but that nobody would call "an unread message". */
export function countsAsUnread(m, myUid) {
  if (!m || m.senderId === myUid) return false;
  if (m.isDeleted || m.deleted) return false;
  /* 🟢 one predicate, shared with the phone — see services/ghostRows.js */
  if (isGhostRow(m)) return false;
  // a call you MISSED is a thing to come back to; one you answered or declined isn't
  if (m.systemType === 'call_history' || m.type === 'missed_call') {
    const outcome = m.callData?.outcome;
    return outcome === 'missed' || outcome === 'cancelled' || outcome === 'busy';
  }
  // 🟥 EVERY OTHER SYSTEM ROW IS NOISE, whatever its systemType. Naming only the three
  //    we knew about (reaction/vote/delete_command) let 'group_created',
  //    'member_added', 'security_code_changed' and anything added later fall through to
  //    `true` — so joining a group would announce "4 unread messages" before anyone
  //    had said a word. Denylist by shape, not by enumeration.
  if (m.type === 'system' || m.systemType) return false;
  return true;
}

/** The number to put on the chat-list row. */
export function unreadFor(chat, uid, newestMs, newestSenderId, myReadAt = 0) {
  const n = Number(chat?.unreadCounts?.[uid]) || 0;
  if (n <= 0) return 0;
  if (newestSenderId && newestSenderId === uid) return 0;   // my own message is never unread
  // 🟥 THE VETO NEEDS A TIMESTAMP THAT SURVIVES THE SWEEP. Using only the newest
  //    surviving message doc disabled this check for exactly the chats it exists for:
  //    once the server deletes a delivered 1:1 message there IS no doc, `newestMs` is 0,
  //    and a count already read on the phone would sit here forever. The chat doc's own
  //    `timestamp` is written on every send and never swept, so it's the reliable floor —
  //    and taking the max of the two also fixes the inverse case, where an older
  //    surviving doc would suppress a badge for newer messages that really are unread.
  const newest = Math.max(Number(newestMs) || 0, tsMs(chat?.timestamp || chat?.lastMessageTime));
  // 🟢 TWO WATERMARKS VETO A BADGE, and the second one matters more than it looks.
  //    `readUpTo` is only written when read receipts are ON — it is the sender's
  //    receipt as well as my read position. `myReadAt` is the private one from
  //    users/{me}/chatReads, written on every read regardless of the setting, so a
  //    chat opened on another device of mine clears here even with receipts off.
  const readTo = Math.max(readUpToMs(chat, uid), Number(myReadAt) || 0);
  if (newest && readTo >= newest) return 0;
  return n;
}

/** True when this account has pinned the chat (the phone's long-press → Pin). */
export const isPinned = (chat, uid) => !!chat?.pinned?.[uid];

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 THE ROW HAS TWO WITNESSES AND USED TO BELIEVE THE WRONG ONE ON PRINCIPLE.

   A chat-list row can learn what the newest message is from exactly two places,
   and neither is a live feed of the conversation:

     · a SURVIVING Firestore message document (services/previews.js), and
     · the phone's EXPORTED history (services/historySync.js), a snapshot.

   The old rule was `preview?.text || fallbackText` — the document wins, always.
   That reads as a sensible order of preference, live before snapshot, and it is
   wrong, because the two are not describing the same moment.

   🟥 STORE-AND-FORWARD DELETES DOCUMENTS UNEVENLY. The sweep runs on the
      RECEIVING phone, on chat open, and skips the reader's own messages
      (ChatModal ~16645), along with albums, locations, tombstones and most
      system rows — which are never swept by anyone. So after a day of texting
      from the phone, what is left in Firestore is an arbitrary older subset,
      and "the newest surviving document" is routinely NOT "the newest message".

      The row then pinned itself to that document and stopped moving: a whole
      day of conversation sat in the export, rendered nowhere, while the list
      went on quoting the last thing this browser had sent — which is precisely
      the class of message that outlives everything else, because your own phone
      never sweeps your own messages.

   🟢 SO RANK THEM BY TIME, NOT BY SOURCE. Both carry milliseconds by the time
      they get here, so there is nothing to reconcile — the question "which of
      these two is newer" has an answer, and the row should ask it.

   🟢 A TIE GOES TO THE LIVE COPY. When both describe the same message they are
      the same sentence, but only the document has the receipts, the thumbnail
      and — the case that matters — the delete-for-everyone tombstone. The
      export was taken before the delete and still holds the original words.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Which of the row's two witnesses is describing the newest message.
 * @param live      {text, ts, senderId, source:'live'}   — the surviving document
 * @param exported  {text, ts, senderId, source}          — chatPreviewFrom()
 */
export function newerPreview(live, exported) {
  const said = (p) => !!(p && p.text);
  if (!said(live)) return said(exported) ? exported : (live || exported || null);
  if (!said(exported)) return live;
  /* 🟥 THIS RANKED BY THE SENDER'S CLOCK, AND TWO PHONES ARE SECONDS APART. A
     message that genuinely arrived second can carry the earlier stamp, so the
     row described the wrong one — the same fault the chat list had on the phone.
     `isNewerThan` prefers the server's seq, which is the only key both devices
     agree on, and falls back to the clock only when there is nothing else. */
  return isNewerThan(exported, live) ? exported : live;
}
